import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { RoleEditPermissions } from "@/pages/settings/roles/edit/components/RoleEditPermissions.tsx";
import useRoleEditService, {
  getRolePermissionTables,
} from "@/pages/settings/roles/edit/role-edit-service.ts";
import {
  PermissionOptionsInterface,
  PermissionsItem,
  PermissionsSection,
} from "@/pages/settings/roles/edit/role-edit.types.ts";
import {
  EditRoleSchema,
  EditRoleSchemaInput,
} from "@/pages/settings/roles/edit/use-role-edit-schema.hook.ts";
import { useAllTables, useSectionFields } from "@/queries/use-custom-fields.query.ts";
import { ApiFieldTypes, ApiCFEntityTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { ApiRole, PermissionType, RoleEditTab, RoleType } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { Box, Stack, TabItem, Tabs, Title } from "@skailer-com/uikit";
import { useCallback, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDocumentFoldersList } from "@/queries/use-documents.query.ts";
import { ApiFolderType } from "@/types/document.types.ts";
import { useWorkflowsShared } from "@/queries/workflows/workflow.query.ts";

export interface RoleEditTabsProps {
  role?: ApiRole | undefined;
  formHook: UseFormReturn<EditRoleSchemaInput, unknown, EditRoleSchema>;
  dataAccessActive?: boolean;
}

export function RoleEditTabs({ role, formHook, dataAccessActive }: RoleEditTabsProps) {
  const { t } = useTranslation();
  const { getFieldTitle } = useCustomFieldsHook();
  const { isFeatureEnabled } = usePermissions();
  const { getEmployeeFieldPermissionSections, getFeaturesPermissionsSections } =
    useRoleEditService();
  const isCustomFieldsEnabled = isFeatureEnabled(ApiFeatureType.custom_fields);
  const { data: fields } = useSectionFields(ApiCFEntityTypes.employee, isCustomFieldsEnabled);
  const { data: allTables } = useAllTables(isCustomFieldsEnabled);
  const permissionTables = useMemo(() => getRolePermissionTables(allTables), [allTables]);
  const { data: folders } = useDocumentFoldersList(ApiFolderType.employee);
  const isWorkflowsEnabled = isFeatureEnabled(ApiFeatureType.workflows);
  const { data: workflows } = useWorkflowsShared(isWorkflowsEnabled);

  const showApiPermissionTabs = [
    RoleType.all_employees,
    RoleType.team_lead,
    RoleType.manager,
    RoleType.custom,
  ].includes(role?.type || RoleType.custom);

  const { watch, setValue } = formHook;
  const roleTab = watch("role_tab");

  const employeeFieldPermissionSections = useCallback(() => {
    const customFieldItems: PermissionsItem[] = (fields || [])
      .filter((field) => field.type == ApiFieldTypes.custom)
      .map((field): PermissionsItem => ({
        title: getFieldTitle(field),
        description: field.description,
        field: `employee_custom_field_permissions.${field.slug}`,
        availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
        renderAs: PermissionOptionsInterface.select,
      }));

    const foldersItems: PermissionsItem[] = (folders || []).map((folders): PermissionsItem => ({
      title: folders.title,
      field: `employee_folder_permissions.${folders.id}`,
      availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
      availableOptionsDescription: {
        [PermissionType.none]: t("core:employee_folder_permissions_none_description"),
        [PermissionType.view]: t("core:employee_folder_permissions_view_description"),
        [PermissionType.edit]: t("core:employee_folder_permissions_edit_description"),
      },
      renderAs: PermissionOptionsInterface.select,
    }));

    const workflowItems: PermissionsItem[] = (workflows || []).map((workflow): PermissionsItem => ({
      title: workflow.public_title ?? workflow.title,
      description: workflow.description ?? undefined,
      field: `employee_workflow_permissions.${workflow.id}.run`,
      availableOptions: [PermissionType.none, PermissionType.edit],
      customPermissionTypesTexts: {
        [PermissionType.none]: t("common:permission_type_none"),
        [PermissionType.edit]: t("core:employee_workflow_permissions_run_edit_title"),
      },
      renderAs: PermissionOptionsInterface.select,
    }));

    const workflowExecutionItems: PermissionsItem[] = (workflows || []).map(
      (workflow): PermissionsItem => ({
        title: workflow.public_title ?? workflow.title,
        description: workflow.description ?? undefined,
        field: `employee_workflow_permissions.${workflow.id}.executions`,
        availableOptions: [PermissionType.none, PermissionType.view, PermissionType.edit],
        customPermissionTypesTexts: {
          [PermissionType.view]: t("core:employee_workflow_permissions_executions_view_title"),
          [PermissionType.edit]: t("core:employee_workflow_permissions_executions_edit_title"),
        },
        availableOptionsDescription: {
          [PermissionType.none]: t(
            "core:employee_workflow_permissions_executions_none_description"
          ),
          [PermissionType.view]: t(
            "core:employee_workflow_permissions_executions_view_description"
          ),
          [PermissionType.edit]: t(
            "core:employee_workflow_permissions_executions_edit_description"
          ),
        },
        renderAs: PermissionOptionsInterface.select,
      })
    );

    const sections: PermissionsSection[] = [
      {
        title: t("custom_fields:custom_fields"),
        groups: [
          {
            title: t("custom_fields:custom_fields"),
            items: customFieldItems,
          },
        ],
      },
      {
        title: t("core:employee_documents"),
        groups: [
          {
            title: t("core:employee_documents"),
            items: foldersItems,
          },
        ],
      },
      ...(workflowItems.length > 0
        ? [
            {
              title: t("workflow:workflows"),
              groups: [
                {
                  title: t("core:employee_workflow_permissions_run_title"),
                  description: t("core:employee_workflow_permissions_run_description"),
                  items: workflowItems,
                },
                {
                  title: t("core:employee_workflow_permissions_executions_title"),
                  description: t("core:employee_workflow_permissions_executions_description"),
                  items: workflowExecutionItems,
                },
              ],
            },
          ]
        : []),
    ];

    const systemSections = getEmployeeFieldPermissionSections();
    const systemFieldTitles = new Map(
      [...(fields ?? []), ...(allTables ?? []).flatMap((table) => table.table_fields)]
        .filter((field) => field.type !== ApiFieldTypes.custom && field.title?.trim())
        .map((field) => [`employee_field_permissions.${field.type}`, getFieldTitle(field)])
    );
    systemSections.forEach((section) => {
      section.groups.forEach((group) => {
        group.items.forEach((item) => {
          item.title = systemFieldTitles.get(item.field) ?? item.title;
        });
      });
    });
    const systemTableTargets: Partial<
      Record<ApiTableTypes, { sectionField: string; groupField?: string }>
    > = {
      [ApiTableTypes.employee_positions]: {
        sectionField: "employee_field_permissions.manage_employee_position",
      },
      [ApiTableTypes.employee_employments]: {
        sectionField: "employee_field_permissions.manage_employee_employment",
      },
      [ApiTableTypes.employee_terminations]: {
        sectionField: "employee_field_permissions.manage_employee_termination",
      },
      [ApiTableTypes.employee_compensations]: {
        sectionField: "employee_field_permissions.manage_employee_compensation",
      },
      [ApiTableTypes.employee_additional_compensations]: {
        sectionField: "employee_field_permissions.manage_employee_compensation",
        groupField: "employee_field_permissions.employee_additional_compensation",
      },
    };

    permissionTables
      .filter((table) => table.table_type !== ApiTableTypes.custom)
      .forEach((table) => {
        const target = systemTableTargets[table.table_type];
        if (!target) return;

        const section = systemSections.find((item) =>
          item.groups.some((group) =>
            group.items.some((permission) => permission.field === target.sectionField)
          )
        );
        const group = target.groupField
          ? section?.groups.find((item) =>
              item.items.some((permission) => permission.field === target.groupField)
            )
          : section?.groups.find((item) => item.title === t("common:view"));

        group?.items.push(
          ...table.table_fields.map((field): PermissionsItem => ({
            title: getFieldTitle(field),
            description: field.description,
            field: `employee_custom_table_field_permissions.${table.id}.fields_permissions.${field.slug}`,
            availableOptions: [PermissionType.none, PermissionType.view],
            renderAs: PermissionOptionsInterface.select,
          }))
        );
      });

    const customTables = permissionTables.filter(
      (table) => table.table_type === ApiTableTypes.custom
    );
    const customTableSections: PermissionsSection[] = customTables.map((table, index) => {
      const isHistoricalTable = table.is_historical ?? false;
      const manageField =
        `employee_custom_table_field_permissions.${table.id}.manage_table` as const;

      const viewItems: PermissionsItem[] = [];

      if (isHistoricalTable) {
        viewItems.push(
          {
            title: t("common:history"),
            field: `employee_custom_table_field_permissions.${table.id}.history`,
            availableOptions: [PermissionType.none, PermissionType.view],
            renderAs: PermissionOptionsInterface.select,
          },
          {
            title: t("common:date_effective_from"),
            field: `employee_custom_table_field_permissions.${table.id}.effective_from`,
            availableOptions: [PermissionType.none, PermissionType.view],
            renderAs: PermissionOptionsInterface.select,
          }
        );
      }

      viewItems.push({
        title: t("core:changed_by"),
        field: `employee_custom_table_field_permissions.${table.id}.changed_by`,
        availableOptions: [PermissionType.none, PermissionType.view],
        renderAs: PermissionOptionsInterface.select,
      });

      viewItems.push(
        ...table.table_fields.map((field): PermissionsItem => ({
          title: field.title,
          description: field.description,
          field: `employee_custom_table_field_permissions.${table.id}.fields_permissions.${field.slug}`,
          availableOptions: [PermissionType.none, PermissionType.view],
          renderAs: PermissionOptionsInterface.select,
        }))
      );

      return {
        title: table.title,
        sectionHeader: index === 0 ? t("custom_fields:custom_tables") : undefined,
        groups: [
          {
            title: table.title,
            items: [
              {
                title: t("common:full_edit_access"),
                description: t("core:grant_access_to_table"),
                field: manageField,
                availableOptions: [PermissionType.none, PermissionType.edit],
                renderAs: PermissionOptionsInterface.switch,
              },
            ],
          },
          {
            title: t("common:view"),
            hiddenWhenField: manageField,
            hiddenWhenValue: PermissionType.edit,
            items: viewItems,
          },
        ],
      };
    });

    return [...systemSections, ...sections, ...customTableSections];
  }, [fields, allTables, folders, permissionTables, workflows]);

  const activeTabIndex = useMemo(() => {
    if (roleTab === RoleEditTab.feature && showApiPermissionTabs) return 1;
    return 0;
  }, [roleTab, showApiPermissionTabs]);

  return (
    <Stack direction={"vertical"} gap={"md"}>
      <Title header={t("core:set_permissions")} size={"lg"} paddingBottom paddingTop />
      <Box className={"border border-dark/10 rounded-xs"}>
        <Tabs
          activeTab={activeTabIndex}
          onActiveTabChange={(activeTab) => {
            setValue("role_tab", [RoleEditTab.data, RoleEditTab.feature][activeTab]);
          }}
        >
          <TabItem title={t("core:peoples_data")}>
            <Stack className={"px-10 pb-10 pt-6"}>
              <RoleEditPermissions
                formHook={formHook}
                getPermissionSections={employeeFieldPermissionSections}
                withBulkActions
              />
            </Stack>
          </TabItem>
          {showApiPermissionTabs && (
            <TabItem title={t("core:features")}>
              <Stack className={"px-10 pb-10 pt-6"}>
                <RoleEditPermissions
                  formHook={formHook}
                  getPermissionSections={getFeaturesPermissionsSections}
                  dataAccessActive={dataAccessActive}
                />
              </Stack>
            </TabItem>
          )}
        </Tabs>
      </Box>
    </Stack>
  );
}
